import { useCallback } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentMethod } from '@stripe/stripe-js';
import { AlertCode, PSP } from 'shared/types/checkout';
import {
  getAccountId,
  setAlert,
  setPaymentCTAState,
  useAppDispatch,
  useAppSelector,
} from 'store';
import { useCheckout } from '../useCheckout';
import { ValidationError } from './errors';
import { validatePaymentInfoForStripe } from './helpers';
import { PlaceStripeOrderOptions } from './types';

export const useStripeOptions = () => {
  const dispatch = useAppDispatch();
  const customerId = useAppSelector(getAccountId);

  const elements = useElements();
  const stripe = useStripe();

  const { placeOrder } = useCheckout();

  const placeStripeOrder = useCallback(
    async ({
      address: { state, stateCode, ...address },
      data,
    }: PlaceStripeOrderOptions) => {
      dispatch(setPaymentCTAState({ psp: PSP.STRIPE, value: true }));

      let paymentMethod: PaymentMethod;
      try {
        paymentMethod = await validatePaymentInfoForStripe({
          stripe,
          elements,
          billingDetails: {
            state: stateCode === 'not-required' ? state : stateCode,
            ...address,
          },
        });
      } catch (error) {
        dispatch(setPaymentCTAState({ psp: PSP.STRIPE, value: false }));

        const isSilent = error instanceof ValidationError && error.silent;
        const errorMessage =
          error instanceof ValidationError
            ? error.message
            : `Validating payment info failed with error:${
                JSON.stringify(error) ?? 'unknown'
              }`;

        if (!isSilent) {
          console.error(
            `Order placement error via ${PSP.STRIPE} for customer:${customerId}. ${errorMessage}`,
          );
          dispatch(setAlert({ code: AlertCode.PAYMENT_PROCESSING_FAILED }));
        }
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      placeOrder({
        ...data,
        paymentMethodType:
          (paymentMethod.card?.wallet?.type as string) ?? paymentMethod.type,
        psp: PSP.STRIPE,
        pspData: paymentMethod.id,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId, stripe, elements],
  );

  return { placeStripeOrder };
};
